import { Search, Check } from "lucide-react";

const Frame = ({ title, children, testid }) => (
  <div className="overflow-hidden rounded-md border border-zinc-200 bg-white text-[10px] text-zinc-700 shadow-sm" data-testid={testid}>
    <div className="flex items-center gap-2 bg-[#0f1f3d] px-2 py-1.5 text-white">
      <span className="h-2 w-2 rounded-sm bg-[#50e6ff]" />
      <span className="truncate">Microsoft Azure · {title}</span>
    </div>
    <div className="space-y-1.5 p-2.5">{children}</div>
  </div>
);

const Field = ({ label, value, hl }) => (
  <div>
    <p className="text-[9px] text-zinc-500">{label}</p>
    <div className={`mt-0.5 truncate rounded border px-1.5 py-1 font-mono ${hl ? "border-sky-400 bg-sky-50 text-sky-900" : "border-zinc-200 bg-zinc-50"}`}>{value}</div>
  </div>
);

const Radio = ({ on, children }) => (
  <p className="flex items-start gap-1.5"><span className={`mt-[3px] h-2 w-2 shrink-0 rounded-full border ${on ? "border-sky-600 bg-sky-600" : "border-zinc-400"}`} />{children}</p>
);

const Btn = ({ children, primary }) => (
  <span className={`inline-block rounded-sm px-2 py-0.5 ${primary ? "bg-sky-700 text-white" : "border border-zinc-300"}`}>{children}</span>
);

const Blade = ({ items, active }) => (
  <div className="flex gap-2">
    <ul className="w-24 shrink-0 space-y-1 border-r border-zinc-100 pr-2">
      {items.map((it) => <li key={it} className={`truncate rounded px-1 ${it === active ? "bg-sky-50 font-medium text-sky-800" : "text-zinc-500"}`}>{it}</li>)}
    </ul>
  </div>
);

const NAV = ["Overview", "Authentication", "Certificates & secrets", "API permissions"];

export const AzureMock = ({ kind, redirectUri }) => {
  const uri = redirectUri || "https://your-domain/api/onedrive/callback";
  if (kind === "portal") return (
    <Frame title="Home" testid="azure-mock-portal">
      <div className="flex items-center gap-1 rounded border border-zinc-200 px-1.5 py-1 text-zinc-400"><Search size={9} />App registrations</div>
      <div className="flex items-center justify-between rounded bg-zinc-50 px-1.5 py-1"><span>App registrations</span><Btn primary>+ New registration</Btn></div>
    </Frame>
  );
  if (kind === "register") return (
    <Frame title="Register an application" testid="azure-mock-register">
      <Field label="Name" value="Resonance Harbor" />
      <p className="text-[9px] text-zinc-500">Supported account types</p>
      <Radio>Accounts in this organizational directory only</Radio>
      <Radio>Accounts in any organizational directory</Radio>
      <Radio on>Accounts in any organizational directory and personal Microsoft accounts</Radio>
      <Radio>Personal Microsoft accounts only</Radio>
      <p className="text-[9px] text-zinc-500">Redirect URI</p>
      <div className="flex gap-1"><div className="rounded border border-sky-400 bg-sky-50 px-1.5 py-1 text-sky-900">Web ▾</div><div className="min-w-0 flex-1 truncate rounded border border-zinc-200 bg-zinc-50 px-1.5 py-1 font-mono">{uri}</div></div>
      <Btn primary>Register</Btn>
    </Frame>
  );
  if (kind === "overview") return (
    <Frame title="Resonance Harbor" testid="azure-mock-overview">
      <div className="flex gap-2"><Blade items={NAV} active="Overview" />
        <div className="min-w-0 flex-1 space-y-1.5">
          <Field label="Display name" value="Resonance Harbor" />
          <Field label="Application (client) ID" value="2d8e91f9-xxxx-xxxx-xxxx-xxxxxxxxxxxx" hl />
          <Field label="Directory (tenant) ID" value="xxxxxxxx-…" />
        </div></div>
    </Frame>
  );
  if (kind === "secret") return (
    <Frame title="Certificates & secrets" testid="azure-mock-secret">
      <div className="flex gap-2"><Blade items={NAV} active="Certificates & secrets" />
        <div className="min-w-0 flex-1 space-y-1.5">
          <Btn>+ New client secret</Btn>
          <table className="w-full"><thead><tr className="text-left text-[9px] text-zinc-500"><th>Description</th><th>Expires</th><th>Value</th><th>Secret ID</th></tr></thead>
            <tbody><tr><td>resonance</td><td>24 mo</td><td className="rounded bg-sky-50 font-mono text-sky-900">Abc8Q~…</td><td className="font-mono text-zinc-400">1f2e…</td></tr></tbody></table>
          <p className="text-[9px] text-amber-700">Copy the Value now — it is hidden after you leave this page.</p>
        </div></div>
    </Frame>
  );
  if (kind === "permissions") return (
    <Frame title="API permissions" testid="azure-mock-permissions">
      <div className="flex gap-2"><Blade items={NAV} active="API permissions" />
        <div className="min-w-0 flex-1 space-y-1">
          <Btn>+ Add a permission</Btn>
          <p className="text-[9px] text-zinc-500">Microsoft Graph · Delegated</p>
          {["Files.ReadWrite", "User.Read", "offline_access"].map((p) => <p key={p} className="flex items-center gap-1.5"><span className="flex h-2.5 w-2.5 items-center justify-center rounded-sm bg-sky-600 text-white"><Check size={7} /></span><span className="font-mono">{p}</span></p>)}
          <Btn primary>Add permissions</Btn>
        </div></div>
    </Frame>
  );
  return (
    <Frame title="Authentication" testid="azure-mock-auth">
      <div className="flex gap-2"><Blade items={NAV} active="Authentication" />
        <div className="min-w-0 flex-1 space-y-1">
          <p className="font-medium">Web · Redirect URIs</p>
          <div className="truncate rounded border border-zinc-200 bg-zinc-50 px-1.5 py-1 font-mono text-zinc-500">https://your-other-domain/api/onedrive/callback</div>
          <div className="truncate rounded border border-sky-400 bg-sky-50 px-1.5 py-1 font-mono text-sky-900">{uri}</div>
          <p className="text-sky-700">+ Add URI</p>
          <Btn primary>Save</Btn>
        </div></div>
    </Frame>
  );
};
